import {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {Column} from 'components/table/Column';
import {useAppSelector, useTypedAppSelector} from 'redux/hooks';
import {useAppDispatch} from 'redux/store';
import {navigationActions} from 'services/navigation/slice';
import {campaignActions, campaignEndpoints} from 'services/campaign/slice';
import {useNavigate, useLocation, UNSAFE_NavigationContext as NavigationContext} from 'react-router-dom';
import {hubspotEndpoints} from 'services/hubspot/slice';
import {useToast} from 'components/Toast';
import {taskCodeMap} from './constants';
import {getResult} from './helpers';
import {useProfile} from './useProfile';
import {ListLoaded, ListResult} from './types';

export type AllTaskCodes =
  | 'analysis_batch_periods'
  | 'analysis_batch_control_groups'
  | 'area_analysis_batch_periods'
  | 'area_analysis_batch_control_groups'
  | 'analysis'
  | 'area_analysis'
  | 'import_sales_data'
  | keyof typeof taskCodeMap;

export const useCampaignTaskActive = (types: AllTaskCodes[] | RegExp): [boolean, boolean] => {
  const wasActiveRef = useRef(false);
  const currentValue = useAppSelector(
    state =>
      getResult(state.campaignTask.tasks).results.filter(
        it => (types instanceof RegExp ? types.test(it.code) : types.includes(it.code)) && it.status !== 'FAILURE',
      ).length > 0,
  );
  if (currentValue) {
    wasActiveRef.current = true;
  }
  return [currentValue, wasActiveRef.current];
};

export const useTaskCompletedShouldReload = (types: AllTaskCodes[] | RegExp) => {
  const wasActiveRef = useRef(false);
  const [shouldRefresh, setShouldRefresh] = useState(false);
  const isPendingStates = ['IN_PROGRESS', 'PROGRESS', 'PENDING'] as const;
  const matcher: (code: string) => boolean =
    types instanceof RegExp ? (code: string) => types.test(code) : (code: string) => types.includes(code as any);
  const isRunning = useTypedAppSelector(state =>
    getResult(state.campaignTask.tasks).results.find(
      it => matcher(it.code as any) && isPendingStates.includes(it.status as any),
    ),
  );
  if (!wasActiveRef.current && isRunning) {
    wasActiveRef.current = true;
  } else if (wasActiveRef.current && !isRunning) {
    wasActiveRef.current = false;
    setShouldRefresh(true);
  }
  return shouldRefresh;
};
export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (...args: any[]) => void;
}
export const useModal = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const openModal = useCallback(() => setIsModalOpen(true), []);
  const closeModal = useCallback(() => setIsModalOpen(false), []);
  return {isModalOpen, openModal, closeModal};
};

export const useRestoreCampaignFromHistory = (desiredPathOrWillDefault?: string) => {
  const appDispatch = useAppDispatch();
  const {state, search} = useLocation<any>();
  const navigate = useNavigate();
  const contextCampaign = useAppSelector(s => s.navigation.contextCampaign);
  const {profile} = useProfile();
  const location = useLocation();
  const path = desiredPathOrWillDefault || location.pathname;

  useEffect(() => {
    const urlCampaignId = new URLSearchParams(search).get('campaign');
    if (urlCampaignId) {
      (async () => {
        const payload = await appDispatch(
          campaignEndpoints.getCampaign({
            campaign_id: urlCampaignId,
          }),
        ).unwrap();
        appDispatch(navigationActions.setContextCampaign(payload));
      })();
    } else if (state?.selectedCampaign) {
      appDispatch(navigationActions.setContextCampaign(state.selectedCampaign));
      (async () => {
        const payload = await appDispatch(
          campaignEndpoints.getCampaign({
            campaign_id: state.selectedCampaign.id,
          }),
        ).unwrap();
        appDispatch(navigationActions.updateContextCampaign(payload));
      })();
    } else if (!contextCampaign && profile?.defaultCampaign) {
      appDispatch(navigationActions.setContextCampaign(profile?.defaultCampaign));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.defaultCampaign?.id]);

  useEffect(() => {
    if (contextCampaign) {
      const params = new URLSearchParams(search);
      params.set('campaign', String(contextCampaign.id));
      navigate(`${path}?${params.toString()}`, {state: {...state, selectedCampaign: contextCampaign}});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, contextCampaign]);
};

export const useCampaignAccess = () => {
  const contextCampaign = useAppSelector(state => state.navigation.contextCampaign);
  const isReadOnlyAccess = contextCampaign?.user_access === 'Viewer';
  return {isReadOnlyAccess};
};

export const useCampaignSalesPartition = () => {
  const contextCampaign = useAppSelector(state => state.navigation.contextCampaign);
  const isSalesPartitionPresent = contextCampaign?.is_sales_partition_present === true;
  return {isSalesPartitionPresent};
};

export const useIsMounted = () => {
  const [isMounted, setMounted] = useState<boolean>(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, [setMounted]);

  return {
    isMounted,
  };
};

/** Hook that provides a refreshContextCampaign function that can be used to
 * refetch the context campaign's data.
 */
export const useRefreshContextCampaign = () => {
  const contextCampaign = useAppSelector(state => state.navigation.contextCampaign);
  const [loading, setLoading] = useState(false);
  const appDispatch = useAppDispatch();

  const refreshContextCampaign = useCallback(async () => {
    if (contextCampaign?.id) {
      setLoading(true);
      try {
        const campaignPayload = await appDispatch(
          campaignEndpoints.getCampaign({campaign_id: contextCampaign.id}),
        ).unwrap();
        await appDispatch(navigationActions.updateContextCampaign(campaignPayload));
      } finally {
        setLoading(false);
      }
    }
  }, [contextCampaign?.id, appDispatch]);

  return {refreshContextCampaign, loading};
};

export const useMainCampaign = () => {
  const appDispatch = useAppDispatch();
  const contextCampaign = useAppSelector(state => state.navigation.contextCampaign);
  const associatedCampaigns = useAppSelector(state => state.campaign.associatedCampaignObjects);

  useEffect(() => {
    if (!contextCampaign) appDispatch(campaignActions.clearAssociatedCampaignObjects());
    if (!contextCampaign) return;

    // If context campaign is the main campaign, no need to fetch associated campaigns
    // (the context itself is the main). Don't clear - other components may use them.
    if (contextCampaign.main_campaign) return;

    appDispatch(campaignEndpoints.getAssociatedCampaignObjects(contextCampaign.id));
  }, [appDispatch, contextCampaign]);

  if (!contextCampaign) return {mainCampaign: undefined};

  if (contextCampaign.main_campaign) return {mainCampaign: contextCampaign};

  const mainCampaign = associatedCampaigns.find(campaign => campaign.main_campaign);
  return {mainCampaign};
};

export const useBlockerBackport = (
  shouldBlock: boolean,
  onNavigateAway: (props: {location: any; retry: () => void}) => void,
) => {
  const {navigator} = useContext(NavigationContext);

  useEffect(() => {
    if (!shouldBlock) return () => {};

    const {push} = navigator;

    navigator.push = (...args: any[]) => {
      const [to, state] = args;
      const location =
        typeof to === 'string'
          ? {pathname: to, search: '', hash: '', state, key: Math.random().toString(36).substr(2, 9)}
          : to;

      // Pass the navigation attempt to your modal or logic
      onNavigateAway({
        location,
        retry: () => {
          navigator.push = push; // restore original push
          // @ts-ignore
          navigator.push(...args);
        },
      });
    };

    return () => {
      navigator.push = push;
    };
  }, [shouldBlock, onNavigateAway, navigator]);
};

export function useCustomColumnFilters<T extends Column[]>(
  columns: T,
  filter: any,
  onFilterChange: (name: string, value: any) => void,
  /** ## Must be not change. Will be empty memo'd for convinience @warn */
  filtersMap: Record<T[number]['name'], (props: {value: any; onChange: (value: any) => void}) => React.ReactElement>,
) {
  const fMap = useMemo(() => {
    return filtersMap;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return useMemo(() => {
    return columns.map(col => {
      return {
        ...col,
        ...(col.name in fMap && {
          filterable: true,
          filter,
          FilterComponent: fMap[col.name],
          onFilterChange,
        }),
      };
    });
  }, [columns, filter, fMap, onFilterChange]);
}

export const useSetContextCampaignFromURLParam = (paramName?: 'campaign', executeAutomatically?: boolean) => {
  const appDispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const {search} = useLocation();
  const campaignId = paramName ? new URLSearchParams(search).get(paramName) : undefined;
  const contextCampaign = useAppSelector(state => state.navigation.contextCampaign);

  const updateContextCampaignFromURLParam = useCallback(async () => {
    setLoading(true);
    try {
      if (!campaignId) return;
      if (Number(contextCampaign?.id) === Number(campaignId)) return;
      const payload = await appDispatch(
        campaignEndpoints.getCampaign({
          campaign_id: campaignId,
        }),
      ).unwrap();
      await appDispatch(navigationActions.setContextCampaign(payload));
    } finally {
      setLoading(false);
    }
  }, [campaignId, appDispatch, contextCampaign?.id]);

  useEffect(() => {
    if (executeAutomatically && paramName) {
      setLoading(true);
      (async () => {
        await updateContextCampaignFromURLParam();
      })();
    }
  }, [updateContextCampaignFromURLParam, executeAutomatically, paramName, setLoading]);

  return {updateContextCampaignFromURLParam, loading, setLoading};
};

export const useMarkDataLoadingTasksAsCompleted = (campaignId?: number) => {
  const appDispatch = useAppDispatch();
  const {errorMessage, successMessage} = useToast();
  const [error, setError] = useState<string | null>(null);
  const [isMarkDataLoadingTasksAsCompletedLoading, setLoading] = useState(false);
  const markDataLoadingTasksAsCompleted = useCallback(async (): Promise<string | null> => {
    if (!campaignId) return null;
    setError(null);
    setLoading(true);
    try {
      const payload = await appDispatch(hubspotEndpoints.markDataLoadingTasksAsCompleted({campaignId})).unwrap();
      successMessage(payload.detail || 'Tasks marked as completed successfully.');
    } catch (e) {
      let errorText: string = 'Failed to mark tasks as completed.';
      if (e instanceof Object) {
        if ('message' in e) {
          errorText = e.message as string;
        } else if ('detail' in e) {
          errorText = e.detail as string;
        } else {
          errorText = `Could not mark tasks as completed:\n${Object.values(e)
            .map(value => `${value}`)
            .join('\n')}`;
        }
      }
      setError(errorText);
      errorMessage(errorText);
      return errorText;
    } finally {
      setLoading(false);
    }
    return null;
  }, [appDispatch, campaignId, errorMessage, successMessage]);

  return {
    markDataLoadingTasksAsCompleted,
    isMarkDataLoadingTasksAsCompletedLoading,
    error,
  };
};

export function useAlertOnStatus<R extends {as: string}>(
  res: R,
  handlers: Partial<{
    [K in Extract<R['as'], 'success' | 'error'>]?: (
      value: Extract<R, {as: K}>,
    ) => string | void | undefined | boolean | null;
  }>,
) {
  const {errorMessage, successMessage} = useToast();
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    if (res.as !== 'success' && res.as !== 'error') return;
    const invMap = {error: errorMessage, success: successMessage} as const;
    if (res.as === 'error') console.error(res);
    const handler = handlersRef.current[res.as];
    if (!handler) return;
    const result = handler(res as any);
    if (!result) return;
    invMap[res.as](result);
  }, [res, errorMessage, successMessage]);
}
export function useResult<T extends ListResult<any>>(
  resultOfT: T,
): ListLoaded<Extract<T, {as: 'success'}>['results'][number]> {
  const [lastRes, setLastRes] = useState<any>(getResult(resultOfT));
  if (resultOfT.as === 'success' && resultOfT !== lastRes) setLastRes(resultOfT);
  return resultOfT.as === 'success' ? resultOfT : lastRes;
}
